/**
 * Typography used in theme.
 * All custom keys from the old theme are kept (customInput, customSelectSx, inputLabelSx, ...)
 * because pages spread them into sx. Their sizes and paddings are unchanged so forms keep
 * their layout; only colours now come from the design tokens.
 * @param {Object} theme  themeOption built in themes/index.js
 */
export default function themeTypography(theme) {
  const t = theme.t;
  const fontFamily = theme?.customization?.fontFamily || t.fonts.sans;

  return {
    fontFamily,
    h6: {
      fontWeight: 600,
      color: t.text,
      fontSize: '0.75rem'
    },
    h5: {
      fontSize: '0.875rem',
      color: t.text,
      fontWeight: 600
    },
    h4: {
      fontSize: '1rem',
      color: t.text,
      fontWeight: 700
    },
    h3: {
      fontSize: '1.25rem',
      color: t.text,
      fontWeight: 700,
      letterSpacing: '-0.01em'
    },
    h2: {
      fontSize: '1.5rem',
      color: t.text,
      fontWeight: 700,
      letterSpacing: '-0.015em'
    },
    h1: {
      fontSize: '2.125rem',
      color: t.text,
      fontWeight: 700,
      letterSpacing: '-0.02em'
    },
    subtitle1: {
      fontSize: '0.875rem',
      fontWeight: 600,
      color: t.text
    },
    subtitle2: {
      fontSize: '0.75rem',
      fontWeight: 400,
      color: t.text3
    },
    caption: {
      fontSize: '0.75rem',
      color: t.text3,
      fontWeight: 400
    },
    body1: {
      fontSize: '0.875rem',
      fontWeight: 400,
      lineHeight: '1.43em'
    },
    body2: {
      letterSpacing: '0em',
      fontWeight: 400,
      lineHeight: '1.5em',
      color: t.text2
    },
    button: {
      textTransform: 'none',
      fontWeight: 600
    },
    // <Typography variant="mono"> for request numbers, contract numbers, codes.
    mono: {
      fontFamily: t.fonts.mono,
      fontSize: '0.8125rem',
      fontWeight: 400,
      fontVariantNumeric: 'tabular-nums'
    },

    // ----- legacy form helpers (geometry unchanged, colours updated) -----
    customInput: {
      marginTop: 1,
      marginBottom: 1,
      '& > label': {
        top: 23,
        left: 0,
        color: t.text3,
        '&[data-shrink="false"]': {
          top: 5
        }
      },
      '& > div > input': {
        padding: '30.5px 14px 11.5px !important'
      },
      '& legend': {
        display: 'none'
      },
      '& fieldset': {
        top: 0
      }
    },
    customSelect: {
      marginTop: 1,
      marginBottom: 1,
      '& > label': {
        top: 23,
        left: 0,
        color: t.text3,
        '&[data-shrink="false"]': {
          top: 5
        }
      }
    },
    customSelectSx: {
      height: 54,
      '& .MuiOutlinedInput-root': {
        height: '100%'
      },
      '& .MuiSelect-select': {
        padding: '23px 14px',
        display: 'flex',
        alignItems: 'center'
      },
      '& .MuiOutlinedInput-notchedOutline': {
        top: '-5px',
        bottom: '-5px'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: t.text3
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: t.primary
      },
      color: t.text,
      backgroundColor: 'transparent'
    },
    customMultiSelectSx: {
      height: 54,
      '& .MuiOutlinedInput-root': {
        height: '100%'
      },
      '& .MuiSelect-select': {
        padding: '23px 14px',
        display: 'flex',
        alignItems: 'center'
      },
      '& .MuiOutlinedInput-notchedOutline': {
        top: '-5px',
        bottom: '-5px'
      },
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: t.text3
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: t.primary
      },
      color: t.text,
      backgroundColor: 'transparent'
    },
    multiSelectInputLabelSx: {
      '&.MuiInputLabel-shrink': {
        marginTop: -2
      },
      '&.Mui-focused': {
        color: t.primaryText
      },
      '&:hover .Mui-focused': {
        color: t.primaryText
      }
    },
    inputLabelSx: {
      '&.MuiInputLabel-shrink': {
        marginTop: -1
      },
      '&.Mui-focused': {
        color: t.primaryText
      },
      '&:not(.Mui-disabled):hover': {
        color: t.text2
      },
      '&.Mui-disabled': {
        color: t.text3,
        opacity: 0.7
      }
    },
    multiSelectAdditionalInfoInsertInputLabelSx: {
      '&.MuiInputLabel-shrink': {
        marginTop: 2
      },
      '&.Mui-focused': {
        color: t.primaryText
      },
      '&:hover .Mui-focused': {
        color: t.primaryText
      }
    },
    outlinedInputSx: {
      '&:hover .MuiOutlinedInput-notchedOutline': {
        borderColor: t.text3
      },
      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
        borderColor: t.primary
      }
    },
    menuItemSx: {
      '&:hover': {
        backgroundColor: t.hover
      },
      '&.Mui-selected': {
        backgroundColor: t.sel
      },
      '&.Mui-selected:hover': {
        backgroundColor: t.sel
      }
    },

    // ----- layout helpers (sizes unchanged until the layout step) -----
    mainContent: {
      backgroundColor: t.canvas,
      boxShadow: `inset 0 0 0 1px ${t.border}`,
      width: '100%',
      minHeight: 'calc(100vh - 88px)',
      flexGrow: 1,
      padding: '20px',
      marginTop: '88px',
      marginRight: '20px',
      borderRadius: '16px'
    },
    errorPageContent: {
      backgroundColor: t.canvas,
      width: '100%',
      flexGrow: 1,
      minHeight: '88vh',
      borderRadius: '16px'
    },
    cleanPageContent: {
      backgroundColor: t.canvas,
      width: '100%',
      flexGrow: 1,
      minHeight: '88vh',
      padding: '20px',
      borderRadius: '16px'
    },
    menuCaption: {
      fontSize: '0.75rem',
      fontWeight: 600,
      color: t.text3,
      padding: '6px',
      textTransform: 'uppercase',
      letterSpacing: '0.06em',
      marginTop: '10px'
    },
    subMenuCaption: {
      fontSize: '0.6875rem',
      fontWeight: 500,
      color: t.text3,
      textTransform: 'none'
    },
    commonAvatar: {
      cursor: 'pointer',
      borderRadius: '10px'
    },
    smallAvatar: {
      width: '22px',
      height: '22px',
      fontSize: '1rem'
    },
    mediumAvatar: {
      width: '34px',
      height: '34px',
      fontSize: '1.2rem'
    },
    largeAvatar: {
      width: '44px',
      height: '44px',
      fontSize: '1.5rem'
    }
  };
}